/**
 * Hook tự động xác định permKey dựa vào URL hiện tại
 * và trả về canEdit / canView cho trang đó.
 */
import { useLocation } from 'react-router-dom'
import { usePermission } from './usePermission'

// Order: most-specific prefix first → fallback parent → module-level catch-all
// last. `Array.find` returns first match, nên thứ tự quyết định resolve.
// `usePermission` tự fallback sub→parent ("purchase.local_p" → "purchase") nếu
// permissions map chưa có sub-permKey.
const ROUTE_PERM_MAP: Array<{ prefix: string; permKey: string }> = [
  // Purchase Contract tabs (PurchaseContractPage shell, /purchase/contract/<tab>)
  { prefix: '/purchase/contract/list', permKey: 'purchase.local_p' },
  { prefix: '/purchase/contract/forward-pu', permKey: 'purchase.forward' },
  { prefix: '/purchase/contract/npe-contract', permKey: 'purchase.npe' },
  { prefix: '/purchase/contract/amendment', permKey: 'purchase.annex' },
  { prefix: '/purchase/contract/ptbf-fixation', permKey: 'purchase.ptbf' },
  { prefix: '/purchase/contract/npe-fixation', permKey: 'purchase.npe_fix_alloc' },
  { prefix: '/purchase/contract/npe-allocation', permKey: 'purchase.npe_fix_alloc' },
  { prefix: '/purchase/contract/ptbf-rolling', permKey: 'purchase.ptbf' },
  { prefix: '/purchase/contract/references', permKey: 'purchase.refs' },
  { prefix: '/purchase/contract/plans', permKey: 'purchase.local_p' },
  { prefix: '/purchase/contract', permKey: 'purchase.local_p' },
  // Purchase synced standalone routes (App.tsx — /purchase/<segment> không qua
  // PurchaseContractPage shell). Nav menu không link tới đây nhưng row click,
  // wizard, deep link đều navigate vào. Phải gate cùng permKey với tab tương ứng.
  { prefix: '/purchase/local-p', permKey: 'purchase.local_p' },
  { prefix: '/purchase/contracts', permKey: 'purchase' },
  { prefix: '/purchase/npe-contracts', permKey: 'purchase.npe' },
  { prefix: '/purchase/annexes', permKey: 'purchase.annex' },
  { prefix: '/purchase/ptbf-fixations', permKey: 'purchase.ptbf' },
  { prefix: '/purchase/ptbf-rollings', permKey: 'purchase.ptbf' },
  { prefix: '/purchase/npe-fixations', permKey: 'purchase.npe_fix_alloc' },
  { prefix: '/purchase/npe-allocations', permKey: 'purchase.npe_fix_alloc' },
  { prefix: '/purchase/cs-fixations', permKey: 'purchase' },
  { prefix: '/purchase/forward', permKey: 'purchase.forward' },
  { prefix: '/purchase/plans', permKey: 'purchase.local_p' },
  { prefix: '/purchase/refs', permKey: 'purchase.refs' },
  // Catch-all cho purchase route mới — gate bằng `purchase` parent. Phải đặt
  // SAU tất cả prefix cụ thể vì Array.find trả first match.
  { prefix: '/purchase', permKey: 'purchase' },
  { prefix: '/vendors', permKey: 'purchase.vendor' },
  { prefix: '/procurement', permKey: 'procurement' },
  { prefix: '/sales/contract', permKey: 'saleContract' },
  { prefix: '/sales/control', permKey: 'saleControl' },
  { prefix: '/futures', permKey: 'futures' },
  { prefix: '/factory', permKey: 'factory' },
  { prefix: '/matching', permKey: 'matchingPs' },
  { prefix: '/quality', permKey: 'quality' },
  { prefix: '/virtual-warehouse', permKey: 'virtualWarehouse' },
  { prefix: '/master-data', permKey: 'masterData' },
  { prefix: '/settings', permKey: 'setting' },
]

export function usePagePermission() {
  const { pathname } = useLocation()
  const permKey = ROUTE_PERM_MAP.find((m) => pathname.startsWith(m.prefix))?.permKey
  return usePermission(permKey)
}
